import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { LogOut, Lock, Loader2, Mail, Calendar, Users, Download, Search, AlertCircle, MessageCircle, Save, CheckCircle2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'srn_admin_token';

function formatDetail(detail) {
  if (!detail) return 'Something went wrong.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

const Login = ({ onSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/admin/login`, { email, password });
      sessionStorage.setItem(TOKEN_KEY, data.access_token);
      onSuccess(data.user);
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || 'Login failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#faf6ee] flex items-center justify-center px-6" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-[#f3ede0] border border-[#e6dfd0] rounded-3xl p-8 shadow-[0_20px_60px_rgba(0,0,0,0.6)]">
          <div className="w-14 h-14 mx-auto bg-[#c9a84c]/10 border border-[#c9a84c]/20 rounded-2xl flex items-center justify-center mb-6">
            <Lock className="w-6 h-6 text-[#c9a84c]" strokeWidth={2.5} />
          </div>
          <h1 className="text-3xl text-[#152846] text-center font-serif mb-2">Admin Dashboard</h1>
          <p className="text-[#8b94a3] text-center text-sm mb-8">Sign in to manage your portfolio data.</p>

          <form onSubmit={submit} className="space-y-4" data-testid="admin-login-form">
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#8b94a3] font-semibold mb-2">Email</label>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] rounded-xl text-[#152846] placeholder-[#aab2bf] focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/40 focus:border-[#c9a84c]/40 transition-all"
                placeholder="admin@srijitnair.com"
                data-testid="admin-email-input"
              />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.18em] text-[#8b94a3] font-semibold mb-2">Password</label>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-[#faf6ee] border border-[#e6dfd0] rounded-xl text-[#152846] placeholder-[#aab2bf] focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/40 focus:border-[#c9a84c]/40 transition-all"
                placeholder="••••••••"
                data-testid="admin-password-input"
              />
            </div>
            {error && (
              <div className="flex items-start gap-2 px-4 py-3 bg-red-900/20 border border-red-900/40 rounded-xl text-red-300 text-sm" data-testid="admin-login-error">
                <AlertCircle size={16} strokeWidth={2.5} className="mt-0.5 shrink-0" />{error}
              </div>
            )}
            <button
              type="submit"
              disabled={busy}
              className="w-full py-3 bg-[#c9a84c] text-black font-semibold rounded-xl hover:bg-[#dbc073] transition-colors disabled:opacity-60 inline-flex items-center justify-center gap-2"
              data-testid="admin-login-submit"
            >
              {busy ? <><Loader2 size={16} className="animate-spin" />Signing in…</> : 'Sign in'}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

const WhatsAppPanel = ({ authHeader }) => {
  const [template, setTemplate] = useState('');
  const [defaultTpl, setDefaultTpl] = useState('');
  const [credsReady, setCredsReady] = useState(false);
  const [webhookPath, setWebhookPath] = useState('/api/whatsapp/webhook');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/whatsapp/settings`, { headers: authHeader });
      setTemplate(data.template || '');
      setDefaultTpl(data.default_template || '');
      setCredsReady(!!data.credentials_ready);
      setWebhookPath(data.webhook_path || '/api/whatsapp/webhook');
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || 'Failed to load settings.');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSaved(false);
    try {
      await axios.put(`${API}/admin/whatsapp/settings`, { template }, { headers: authHeader });
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (err) {
      setError(formatDetail(err?.response?.data?.detail) || 'Failed to save.');
    } finally {
      setSaving(false);
    }
  };

  const reset = () => setTemplate(defaultTpl);

  const webhookUrl = `${process.env.REACT_APP_BACKEND_URL}${webhookPath}`;

  return (
    <div className="bg-[#f3ede0] border border-[#e6dfd0] rounded-2xl p-6 mb-6" data-testid="whatsapp-template-editor">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-5">
        <div>
          <h2 className="text-xl font-serif text-[#152846] mb-1">Auto-Reply Template</h2>
          <p className="text-sm text-[#8b94a3]">
            Use <code className="text-[#c9a84c]">{'{name}'}</code> for the sender's name and{' '}
            <code className="text-[#c9a84c]">{'{message}'}</code> for their original message.
          </p>
        </div>
        <div className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-full border ${
          credsReady
            ? 'bg-emerald-50 border-emerald-200 text-emerald-700'
            : 'bg-amber-50 border-amber-200 text-amber-700'
        }`} data-testid="whatsapp-creds-status">
          <span className={`w-2 h-2 rounded-full ${credsReady ? 'bg-emerald-500' : 'bg-amber-500'}`} />
          {credsReady ? 'Meta credentials live' : 'Placeholder credentials (dry-run)'}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center text-[#8b94a3] py-8"><Loader2 size={18} className="animate-spin mr-2" />Loading…</div>
      ) : (
        <>
          <textarea
            value={template}
            onChange={(e) => setTemplate(e.target.value)}
            rows={5}
            className="w-full p-4 bg-[#faf6ee] border border-[#e6dfd0] rounded-xl text-[#152846] placeholder-[#aab2bf] focus:outline-none focus:ring-2 focus:ring-[#c9a84c]/40 focus:border-[#c9a84c]/40 transition-all font-mono text-sm leading-relaxed resize-y"
            placeholder="Hi {name}, thanks for your message…"
            data-testid="whatsapp-template-input"
          />

          <div className="mt-4 flex flex-wrap items-center gap-3">
            <button
              onClick={save}
              disabled={saving || !template.trim()}
              className="inline-flex items-center gap-1.5 px-5 py-2 bg-[#c9a84c] text-black font-semibold rounded-xl hover:bg-[#dbc073] transition-colors disabled:opacity-60"
              data-testid="whatsapp-template-save"
            >
              {saving ? <><Loader2 size={14} className="animate-spin" />Saving…</> : <><Save size={14} strokeWidth={2.5} />Save template</>}
            </button>
            <button
              onClick={reset}
              className="px-4 py-2 text-sm text-[#5c6878] border border-[#e6dfd0] rounded-xl hover:border-[#c9a84c]/30 transition-colors"
              data-testid="whatsapp-template-reset"
            >
              Reset to default
            </button>
            {saved && (
              <span className="inline-flex items-center gap-1.5 text-sm text-emerald-700" data-testid="whatsapp-template-saved">
                <CheckCircle2 size={14} strokeWidth={2.5} />Saved
              </span>
            )}
            {error && (
              <span className="inline-flex items-center gap-1.5 text-sm text-red-600">
                <AlertCircle size={14} strokeWidth={2.5} />{error}
              </span>
            )}
          </div>

          <div className="mt-6 pt-5 border-t border-[#e6dfd0]">
            <p className="text-xs uppercase tracking-[0.18em] text-[#8b94a3] font-semibold mb-2">Webhook URL (paste in Meta Developer Console)</p>
            <code className="block p-3 bg-[#faf6ee] border border-[#e6dfd0] rounded-lg text-xs text-[#152846] break-all font-mono" data-testid="whatsapp-webhook-url">
              {webhookUrl}
            </code>
            <p className="text-xs text-[#8b94a3] mt-2">
              Verify-token, Phone-ID and Permanent token live in <code>backend/.env</code>. See <code>/app/whatsapp_setup_runbook.md</code> for the full Meta setup walkthrough.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

const TABS = [
  { id: 'subscribers', label: 'Subscribers', icon: Users, endpoint: '/admin/subscribers', listKey: 'subscribers', columns: ['email', 'subscribed_at', 'source', 'status'] },
  { id: 'contacts', label: 'Contact Messages', icon: Mail, endpoint: '/admin/contacts', listKey: 'contacts', columns: ['name', 'email', 'subject', 'created_at'] },
  { id: 'bookings', label: 'Bookings', icon: Calendar, endpoint: '/admin/bookings', listKey: 'bookings', columns: ['name', 'email', 'phone', 'date', 'time_slot', 'purpose'] },
  { id: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, endpoint: '/admin/whatsapp/messages', listKey: 'messages', columns: ['direction', 'wa_id', 'profile_name', 'body', 'delivery_status', 'created_at'] },
];

const csvEscape = (v) => {
  if (v == null) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const toCsv = (rows, columns) => {
  const header = columns.join(',');
  const body = rows.map((r) => columns.map((c) => csvEscape(r[c])).join(',')).join('\n');
  return header + '\n' + body;
};

const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('subscribers');
  const [data, setData] = useState({ subscribers: [], contacts: [], bookings: [] });
  const [counts, setCounts] = useState({ subscribers: 0, contacts: 0, bookings: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const token = sessionStorage.getItem(TOKEN_KEY);
  const authHeader = { Authorization: `Bearer ${token}` };

  const fetchTab = useCallback(async (tabId) => {
    const tab = TABS.find((t) => t.id === tabId);
    if (!tab) return;
    setLoading(true);
    try {
      const { data: res } = await axios.get(`${API}${tab.endpoint}`, { headers: authHeader });
      setData((d) => ({ ...d, [tab.listKey]: res[tab.listKey] || [] }));
      setCounts((c) => ({ ...c, [tab.listKey]: res.count || 0 }));
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(TOKEN_KEY);
        onLogout();
      }
      console.error('Fetch tab error:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Load counts for all 3 tabs on mount
    TABS.forEach((t) => fetchTab(t.id));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const current = TABS.find((t) => t.id === activeTab);
  const rows = (data[current.listKey] || []).filter((r) => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return current.columns.some((c) => String(r[c] || '').toLowerCase().includes(q));
  });

  const exportCsv = () => {
    const csv = toCsv(rows, current.columns);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${current.id}-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const fmt = (key, val) => {
    if (val == null || val === '') return '—';
    if (key.includes('_at') || key === 'date') {
      const d = new Date(val);
      if (!isNaN(d)) return d.toLocaleString();
    }
    return String(val);
  };

  return (
    <div className="min-h-screen bg-[#faf6ee] text-gray-200" data-testid="admin-dashboard-page">
      <header className="border-b border-[#e6dfd0] bg-[#faf6ee]/80 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/sn-logo.png" alt="SN" className="h-10 w-auto" />
            <span className="text-sm uppercase tracking-[0.2em] text-[#8b94a3] font-semibold">Admin</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline text-sm text-[#5c6878]">{user?.email}</span>
            <button
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm text-[#5c6878] hover:text-[#c9a84c] border border-[#e6dfd0] hover:border-[#c9a84c]/30 rounded-full transition-all"
              data-testid="admin-logout-btn"
            >
              <LogOut size={14} strokeWidth={2.5} />Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-10">
        <h1 className="text-4xl font-serif text-[#152846] mb-2">Dashboard</h1>
        <p className="text-[#8b94a3] mb-8">Newsletter subscribers, contact messages, and appointment bookings.</p>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setActiveTab(t.id)}
              className={`text-left p-5 rounded-2xl border transition-all duration-300 ${
                activeTab === t.id
                  ? 'bg-[#fffdf8] border-[#c9a84c]/40 shadow-[0_10px_30px_-15px_rgba(201,168,76,0.4)]'
                  : 'bg-[#f3ede0] border-[#e6dfd0] hover:border-[#c9a84c]/20'
              }`}
              data-testid={`admin-tab-${t.id}`}
            >
              <div className="flex items-center justify-between mb-3">
                <t.icon size={20} strokeWidth={2.25} className={activeTab === t.id ? 'text-[#c9a84c]' : 'text-[#8b94a3]'} />
                <span className="text-3xl font-serif text-[#152846] tabular-nums">{counts[t.listKey]}</span>
              </div>
              <div className={`text-sm font-medium ${activeTab === t.id ? 'text-[#c9a84c]' : 'text-[#5c6878]'}`}>{t.label}</div>
            </button>
          ))}
        </div>

        {activeTab === 'whatsapp' && <WhatsAppPanel authHeader={authHeader} />}

        <div className="bg-[#f3ede0] border border-[#e6dfd0] rounded-2xl overflow-hidden">
          <div className="flex items-center gap-3 p-4 border-b border-[#e6dfd0]">
            <div className="relative flex-1 max-w-md">
              <Search size={16} strokeWidth={2.5} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#aab2bf]" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={`Search ${current.label.toLowerCase()}…`}
                className="w-full pl-9 pr-3 py-2 bg-[#faf6ee] border border-[#e6dfd0] rounded-lg text-sm text-[#152846] placeholder-[#aab2bf] focus:outline-none focus:border-[#c9a84c]/40"
                data-testid="admin-search-input"
              />
            </div>
            <button
              onClick={exportCsv}
              disabled={rows.length === 0}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-[#c9a84c]/10 text-[#c9a84c] border border-[#c9a84c]/30 rounded-lg hover:bg-[#c9a84c]/20 transition-colors disabled:opacity-50"
              data-testid="admin-export-csv"
            >
              <Download size={14} strokeWidth={2.5} />Export CSV
            </button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-16 text-[#8b94a3]">
              <Loader2 size={20} className="animate-spin mr-2" />Loading…
            </div>
          ) : rows.length === 0 ? (
            <div className="text-center py-16 text-[#8b94a3]" data-testid="admin-empty-state">No {current.label.toLowerCase()} yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid={`admin-table-${current.id}`}>
                <thead>
                  <tr className="text-xs uppercase tracking-[0.12em] text-[#8b94a3] border-b border-[#e6dfd0]">
                    {current.columns.map((c) => (
                      <th key={c} className="text-left px-4 py-3 font-semibold">{c.replace(/_/g, ' ')}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, idx) => (
                    <tr key={r.id || idx} className="border-b border-[#e6dfd0]/50 hover:bg-[#fffdf8]/50 transition-colors">
                      {current.columns.map((c) => (
                        <td key={c} className="px-4 py-3 text-[#3e4858] align-top">{fmt(c, r[c])}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const token = sessionStorage.getItem(TOKEN_KEY);
    if (!token) {
      setChecking(false);
      return;
    }
    axios
      .get(`${API}/admin/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setUser(data))
      .catch(() => sessionStorage.removeItem(TOKEN_KEY))
      .finally(() => setChecking(false));
  }, []);

  const logout = () => {
    sessionStorage.removeItem(TOKEN_KEY);
    setUser(null);
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-[#faf6ee] flex items-center justify-center text-[#8b94a3]">
        <Loader2 size={20} className="animate-spin mr-2" />
      </div>
    );
  }
  if (!user) return <Login onSuccess={setUser} />;
  return <Dashboard user={user} onLogout={logout} />;
};

export default Admin;
